import { copy } from 'ember-copy';
import BaseFilterResponse from './base-filter-response';
import BesselJson from './bessel-json';
import Frequency from '../process-data/frequency';
import FilterConstants from '../../filter-constants';
import LowPass from '../filter/low-pass';
import * as StageUtils from '../utils/stage';
import * as ResultsUtils from '../utils/results';
import * as ConversionUtils from '../utils/conversion';

/**
@function closest_point
@param {int} passbandAttenuation Passband attenuation provided by the user
@param {object} cmag  object containing the magnitude of the order
@returns two closest point indices.
@description <p> This function takes input as the passband attenuation from the user, magnitude array, frequency array finds the two closest points in the magnitude array</p>
<p><b> Search through the magnitude array to find the two points closest to the users specified passband attenuation</p></b>
<p><b>INPUT : User specified passband attenuation, magnitude array  , frequency points</p></b>
<p><b>OUTPUT : closest index 1 , closest index 2</p></b>
*/
var closest_point = function (passbandAttenuation, magArray) {
  var closest1 = magArray[0];
  var prev = Math.abs(magArray[0] - passbandAttenuation);
  for (var s = 0; s < magArray.length; s++) {
    var diff = Math.abs(magArray[s] - passbandAttenuation);

    if (diff < prev) {
      prev = diff;
      closest1 = magArray[s];
    }
  }
  var index1 = magArray.indexOf(closest1); // first closest index obtained.
  magArray.splice(index1, 1);
  var closest2 = magArray[0];
  prev = Math.abs(magArray[0] - passbandAttenuation);
  for (var k = 0; k <= magArray.length; k++) {
    var diff2 = Math.abs(magArray[k] - passbandAttenuation);
    if (diff2 < prev) {
      prev = diff2;
      closest2 = magArray[k];
    }
  }

  magArray.splice(index1, 0, closest1);
  var index2 = magArray.indexOf(closest2);

  return [index1, index2]; // returns indices of two closest point
};

/**
@function linear_interpolate
@param {object} fp  object containing the array of frequency points
@param {object} b  object containing the two closest point indices
@param {object} cmag  object containing the magnitude of the order
@param {int} passbandAttenuation Passband attenuation provided by the user
@returns normalized passband frequency
@description <p> This function takes input as the passband attenuation from the user, magnitude array, two closest points in the magnitude array and returns normalized passband frequency</p>
<p><b> Based on the two points found above, linearly interpolate their corresponding frequency points to find the frequency of the user's specified passband attenuation.We will call this the normalized passband frequency.</p></b>
<p>function to linearly interpolate the frequency array</p>
<p><b>INPUT :</b> frequency point array, closest indices, magnitude array , user specifies passband attenuation</p>
<p><b>OUTPUT :</b> normalized passband frequency</p>
*/
var linear_interpolate = function (fp, b, magArray, passbandAttenuation) {
  var d = b[0];
  var e = b[1];
  var x1 = magArray[d];
  var y1 = fp[d];
  var x2 = magArray[e];
  var y2 = fp[e];
  var x3 = passbandAttenuation;
  var y3 = y1 + ((x3 - x1) * (y2 - y1)) / (x2 - x1);
  var normalized_pass_freq = y3;

  return normalized_pass_freq;
};

/**
@function norm_stop_freq
@param {object} li  object containing the normalized passband frequency.
@param {int} stopbandFrequency Stopband frequency provided by the user
@param {int} passbandFrequency Passband frequency provided by the user
@description function to create normalized stopband frequency
<p> normalized stopband frequency = normalized passband frequency * user's stopband frequency / user's passband frequency</p>
<p>INPUT : normalized passband frequency</p>
<p>OUTPUT :normalized stopband frequency</p>
*/
var norm_stop_freq = function (li, stopbandFrequency, passbandFrequency) {
  var normalized_stop_freq = li * (stopbandFrequency / passbandFrequency);

  return normalized_stop_freq;
};

/**
@function closest_freq_points
@param {object} fp  object containing the array of frequency points
@param {object} p  object containing the normalized stopband frequency
@returns two closest point indices of the frequency point array.
@description <p> This function takes input as the frequency array and normalized stopband frequency and finds the two closest points in the frequency array</p>
<p><b> Search through frequency array to find the two points closest to the normalized stopband frequency</p></b>
<p><b>INPUT : frequency points,normalized stopband frequency</p></b>
<p><b>OUTPUT : closest index 1 , closest index 2</p></b>
*/
var closest_freq_points = function (fp, p) {
  var closestfreq1 = fp[0];
  var prev = Math.abs(fp[0] - p);

  for (var s = 0; s < fp.length; s++) {
    var diff = Math.abs(fp[s] - p);

    if (diff < prev) {
      prev = diff;
      closestfreq1 = fp[s];
    }
  }

  var indexfreq1 = fp.indexOf(closestfreq1);
  fp.splice(indexfreq1, 1);
  var closestfreq2 = fp[0];
  var prev2 = Math.abs(fp[0] - p);
  for (var k = 0; k <= fp.length; k++) {
    var diff2 = Math.abs(fp[k] - p);

    if (diff2 < prev2) {
      prev2 = diff2;
      closestfreq2 = fp[k];
    }
  }

  fp.splice(indexfreq1, 0, closestfreq1);
  var indexfreq2 = fp.indexOf(closestfreq2);

  return [indexfreq1, indexfreq2];
};

/**
@function linear_interpolate_mag
@param {object} fp  object containing the array of frequency points
@param {object} h  object containing the two closest point indices in the frequency array
@param {object} cmag  object containing the magnitude of the order
@param {object} p normalized stop band frequency
@returns normalized passband attenuation
@description <p> This function takes input as the frequency array,normalized stopband frequency, magnitude array, two closest points in the frequency array and returns normalized passband attenuation</p>
<p><b>Based on the two frequency points found above, linearly interpolate the magnitude array to find the calculated stopband attenuation.  You will use this value to determine when you exit the loop </p></b>
<p>function to linearly interpolate the magnitude array</p>
<p><b>INPUT :</b> frequency point array, closest indices,magnitude array,normalized stopband frequency</p>
<p><b>OUTPUT :</b> normalized passband attenuation</p>
*/
var linear_interpolate_mag = function (fp, h, magArray, p) {
  var d = h[0];
  var e = h[1];
  var y1 = magArray[d];
  var x1 = fp[d];
  var y2 = magArray[e];
  var x2 = fp[e];
  var x3 = p;
  var y3 = y1 + ((x3 - x1) * (y2 - y1)) / (x2 - x1);
  var normalized_pass_att = y3;

  return normalized_pass_att;
};

export default class Bessel extends BaseFilterResponse {
  name = FilterConstants.responseType.bessel;

  /**
    Bessel Low Pass Algorithm
    LAST UPDATED : 09/05/2012 2:31 PM
    @author Created by : Archita Ghosh on behalf of Collaborative Consulting and ADI
    @function Bessel_lowpass -This is a Bessel low pass filter algorithm.
    @param {float}  passbandAttenuation -     passband attenuation from the user in dB.   Always a negative number.
    @param {float}  stopbandAttenuation -     stopband attenuation from the user in dB.   Always a negative number.
    @param {float}  passbandFrequency -       passband frequency from the user in Hz.  
    @param {float}  stopbandFrequency -       stopband frequency from the user in Hz.  
    @description This function calculated the Bessel response for low pass filter
    <p>Do until (calculated stopband attenuation < user's specified stopband attenuation) or (loop index > MAXORDER)</p>
    <p>{</p>
    <p>Pull stage information from the 2D array of Bessel values for the order corresponding to the loop index.</p>     
    <p>Compute magnitude array using  Calculating Phase and Magnitude for many stages - Ideal</p>
    <p>Search through the magnitude array to find the two points closest to the users specified passband attenuation.  (Note:   make sure units of what you are comparing are the same:   either V/V or dB)</p>
    <p>Based on the two points found above, linearly interpolate their corresponding frequency points to find the frequency of the user's specified passband attenuation.   We will call this the normalized passband frequency.</p>
    <p>normalized stopband frequency = normalized passband frequency * user's stopband frequency / user's passband frequency</p>
    <p>Search through frequency array to find the two points closest to the normalized stopband frequency.</p>
    <p>Based on the two frequency points found above, linearly interpolate the magnitude array to find the calculated stopband attenuation.  You will use this value to determine when you exit the loop.  (Note: make sure units same: V/V or dB.)</p>
    <p>}</p>
    */
  lowPass(passbandAttenuation, stopbandAttenuation, passbandFrequency, stopbandFrequency) {
    var besselJson = this.createBesselTable(passbandFrequency);
    var orderDiv = this.findAppropriateOrder(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
      besselJson,
    );
    var stageGroup = this.getTargetStageGroup(orderDiv, besselJson);
    var result = StageUtils.createTargetStages(stageGroup.stages);

    return result;
  }

  getTargetStageGroup(orderDiv, besselJson) {
    var result = besselJson.find((item) => {
      return item.nthOrder === orderDiv.order;
    });
    var div = orderDiv.div;
    result.stages.forEach((stage) => {
      stage.stageFp = stage.stageFp * div;
    });

    return result;
  }

  findAppropriateOrder(passbandAttenuation, stopbandAttenuation, passbandFrequency, stopbandFrequency, besselJson) {
    let MAXORDER = FilterConstants.maxOrder;
    var passbandAttenuationVV = ConversionUtils.dBtoVV(passbandAttenuation);
    var stopbandAttenuationVV = ConversionUtils.dBtoVV(stopbandAttenuation);
    var frequencyArray = this._getFrequency(passbandFrequency, stopbandFrequency);
    var besselTable = null;
    var div = null;

    for (var i = 0; i < besselJson.length; i++) {
      var stageGroup = besselJson[i];
      var stages = stageGroup.stages;
      var individualStagesMagnitude = stages.map((stage) => {
        var filterAmplitudeArray = this._computeFilterAmplitude(stage, frequencyArray);
        var stageMagnitude = filterAmplitudeArray.map((filterAmplitude) => {
          return filterAmplitude.magnitude();
        });

        return stageMagnitude;
      });

      var magnitudeArray = ResultsUtils.getMagnitudeArray(individualStagesMagnitude);
      let clMagPoint = closest_point(passbandAttenuationVV, magnitudeArray);
      let normPassFreq = linear_interpolate(frequencyArray, clMagPoint, magnitudeArray, passbandAttenuationVV);
      let normStopFreq = norm_stop_freq(normPassFreq, stopbandFrequency, passbandFrequency);
      let clFreqPoint = closest_freq_points(frequencyArray, normStopFreq);
      let cal_stopbandAttenuationVV = linear_interpolate_mag(frequencyArray, clFreqPoint, magnitudeArray, normStopFreq);
      besselTable = stageGroup;
      div = passbandFrequency / normPassFreq;
      if (cal_stopbandAttenuationVV < stopbandAttenuationVV || i > MAXORDER) {
        break;
      }
    }

    var result = { order: -1, div: div };

    if (besselTable !== null) {
      result.order = besselTable.nthOrder;
    }

    return result;
  }

  getOrder(passbandAttenuation, stopbandAttenuation, passbandFrequency, stopbandFrequency) {
    var besselJson = this.createBesselTable(passbandFrequency);
    var appropriateOrder = this.findAppropriateOrder(
      passbandAttenuation,
      stopbandAttenuation,
      passbandFrequency,
      stopbandFrequency,
      besselJson,
    );
    return appropriateOrder.order;
  }

  /*This function creates a bessel table, scaled by the user's passband frequency, that can be used to calculate the bessel order*/
  createBesselTable(passbandFrequency) {
    var stageGroupArray = copy(BesselJson.orders, true);
    var result = stageGroupArray.map((stageGroup) => {
      stageGroup.stages.forEach((stage) => {
        stage.stageFp = stage.stageFp * passbandFrequency;
      });

      return stageGroup;
    });

    return result;
  }

  _getFrequency(passbandFrequency, stopbandFrequency) {
    const startFrequency = passbandFrequency / 100;
    const stopFrequency = stopbandFrequency * 100;
    const pointsPerDecade = 100;

    const result = Frequency.create({
      startFrequency: startFrequency,
      stopFrequency: stopFrequency,
      pointsPerDecade: pointsPerDecade,
    }).narrowFrequencyArray;

    return result;
  }

  _computeFilterAmplitude(stage, frequencyArray) {
    var result = null;
    var stageFp = stage.stageFp;
    var stageQ = stage.stageQ;
    var gain = stage.gain;

    if (stage.orderType === 1) {
      result = LowPass.createFirstOrderFilterAmplitude(stageFp, gain, frequencyArray);
    } else {
      result = LowPass.createSecondOrderFilterAmplitude(stageFp, stageQ, gain, frequencyArray);
    }

    return result;
  }

  _getMagnitudeArray() {}
}
